/**
 * activityCalculator.js
 *
 * 주간 활동 시각화를 위한 데이터 계산 유틸리티
 * - ISO Week 기준 52주 그룹핑
 * - 주간 집계: 총 조회수(원 크기), 가중 참여율(원 투명도)
 * - 가중 참여율 = 댓글×2 + 리포스트×3 + 좋아요
 */

/**
 * ISO 8601 주 번호 계산
 * @param {Date} date
 * @returns {number} 1-53
 */
export const getISOWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

/**
 * 주의 시작일 (월요일) 계산
 * @param {number} year
 * @param {number} weekNumber
 * @returns {Date}
 */
export const getWeekStartDate = (year, weekNumber) => {
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7;
  const firstMonday = new Date(jan4);
  firstMonday.setDate(jan4.getDate() - dayOfWeek + 1);

  const weekStart = new Date(firstMonday);
  weekStart.setDate(firstMonday.getDate() + (weekNumber - 1) * 7);
  return weekStart;
};

/**
 * 주의 종료일 (일요일) 계산
 * @param {Date} weekStart
 * @returns {Date}
 */
export const getWeekEndDate = (weekStart) => {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  return weekEnd;
};

/**
 * 요일 계산 (월요일=0, 일요일=6)
 * @param {Date} date
 * @returns {number} 0-6
 */
export const getDayOfWeek = (date) => {
  const day = date.getDay();
  // JavaScript: 0=일, 1=월, ... 6=토
  // 변환: 월=0, 화=1, ... 일=6
  return day === 0 ? 6 : day - 1;
};

/**
 * 시간대 계산 (0-24시간, 소수점 포함)
 * @param {Date} date
 * @returns {number} 0-24
 */
export const getHourOfDay = (date) => {
  return date.getHours() + date.getMinutes() / 60;
};

/**
 * 원 크기/투명도 설정
 */
export const CIRCLE_CONFIG = {
  minRadius: 3,
  maxRadius: 40,
  minOpacity: 0.15,
  maxOpacity: 1.0,
  strokeWidth: 1,
};

/**
 * 가중 참여율 계산
 * 댓글×2 + 리포스트×3 + 좋아요
 * @param {Object} data - { replies, reposts, likes }
 * @returns {number}
 */
export const calculateWeightedEngagement = (data) => {
  const replies = data.replies || 0;
  const reposts = data.reposts || 0;
  const likes = data.likes || 0;
  return (replies * 2) + (reposts * 3) + likes;
};

/**
 * 정규화된 값을 원 반지름으로 변환 (지수 스케일링)
 * 작은 값과 큰 값의 편차를 극대화
 * @param {number} normalizedViews 0-1
 * @returns {number} px
 */
export const getCircleRadius = (normalizedViews) => {
  // 지수 스케일링: (e^(x*k) - 1) / (e^k - 1)
  // k=3 → 편차 극대화
  const k = 3;
  const scaled = (Math.exp(normalizedViews * k) - 1) / (Math.exp(k) - 1);
  return CIRCLE_CONFIG.minRadius +
    scaled * (CIRCLE_CONFIG.maxRadius - CIRCLE_CONFIG.minRadius);
};

/**
 * 정규화된 값을 원 투명도로 변환 (제곱근 스케일링)
 * 낮은 참여율도 시인성 확보하면서 편차 유지
 * @param {number} normalizedEngagement 0-1
 * @returns {number} 0-1
 */
export const getCircleOpacity = (normalizedEngagement) => {
  // 제곱근 스케일링: 낮은 값도 어느 정도 보이게
  const scaled = Math.pow(normalizedEngagement, 0.6);
  return CIRCLE_CONFIG.minOpacity +
    scaled * (CIRCLE_CONFIG.maxOpacity - CIRCLE_CONFIG.minOpacity);
};

/**
 * 스레드를 ISO 주 단위로 그룹핑하고 주간 집계 계산
 * @param {Array} threads
 * @param {number} year
 * @returns {Array} 52주 배열 (개별 스레드 포함)
 */
export const groupThreadsByWeek = (threads, year) => {
  // 해당 연도 스레드만 필터링
  const yearThreads = threads.filter(t => {
    const date = new Date(t.timestamp);
    return date.getFullYear() === year;
  });

  // 52주 초기화
  const weeks = Array.from({ length: 52 }, (_, i) => {
    const weekNumber = i + 1;
    const weekStart = getWeekStartDate(year, weekNumber);
    const weekEnd = getWeekEndDate(weekStart);

    return {
      weekNumber,
      weekStart: weekStart.toISOString().split('T')[0],
      weekEnd: weekEnd.toISOString().split('T')[0],
      month: weekStart.getMonth(),
      threadCount: 0,
      totalViews: 0,
      totalLikes: 0,
      totalReplies: 0,
      totalReposts: 0,
      weightedEngagement: 0,
      threads: [], // 개별 스레드 배열 추가
    };
  });

  // 스레드 분류 및 집계
  yearThreads.forEach(thread => {
    const date = new Date(thread.timestamp);
    if (date.getFullYear() !== year) return;

    const weekNumber = getISOWeekNumber(date);
    const weekIndex = Math.min(weekNumber - 1, 51);
    const dayOfWeek = getDayOfWeek(date);

    const week = weeks[weekIndex];
    week.threadCount += 1;
    week.totalViews += (thread.views || 0);
    week.totalLikes += (thread.likes || 0);
    week.totalReplies += (thread.replies || 0);
    week.totalReposts += (thread.reposts || 0);

    // 개별 스레드 정보 저장
    const threadEngagement = calculateWeightedEngagement({
      replies: thread.replies || 0,
      reposts: thread.reposts || 0,
      likes: thread.likes || 0,
    });
    const hourOfDay = getHourOfDay(date);

    week.threads.push({
      id: thread.id,
      timestamp: thread.timestamp,
      weekNumber,
      dayOfWeek,
      hourOfDay,
      views: thread.views || 0,
      likes: thread.likes || 0,
      replies: thread.replies || 0,
      reposts: thread.reposts || 0,
      weightedEngagement: threadEngagement,
      text: thread.text?.substring(0, 50) || '', // 툴팁용 미리보기
      permalink: thread.permalink || null,
    });
  });

  // 가중 참여율 계산
  weeks.forEach(week => {
    week.weightedEngagement = calculateWeightedEngagement({
      replies: week.totalReplies,
      reposts: week.totalReposts,
      likes: week.totalLikes,
    });

    // 개별 스레드를 timestamp 기준 정렬
    week.threads.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  });

  return weeks;
};

/**
 * 주간 데이터 정규화 (크기/투명도 매핑용)
 * @param {Array} weeks - groupThreadsByWeek 결과
 * @returns {Array} 정규화된 주간 배열 (개별 스레드도 정규화)
 */
export const normalizeWeeklyData = (weeks) => {
  // 활동이 있는 주만 추출
  const activeWeeks = weeks.filter(w => w.threadCount > 0);

  if (activeWeeks.length === 0) {
    return weeks.map(w => ({
      ...w,
      normalizedViews: 0,
      normalizedEngagement: 0,
      threads: w.threads.map(t => ({
        ...t,
        normalizedViews: 0,
        normalizedEngagement: 0,
      })),
    }));
  }

  // 주간 집계 범위 계산
  const weekViewsValues = activeWeeks.map(w => w.totalViews);
  const weekEngagementValues = activeWeeks.map(w => w.weightedEngagement);

  const minWeekViews = Math.min(...weekViewsValues);
  const maxWeekViews = Math.max(...weekViewsValues);
  const weekViewsRange = maxWeekViews - minWeekViews || 1;

  const minWeekEngagement = Math.min(...weekEngagementValues);
  const maxWeekEngagement = Math.max(...weekEngagementValues);
  const weekEngagementRange = maxWeekEngagement - minWeekEngagement || 1;

  // 개별 스레드 범위 계산 (전체 스레드 대상)
  const allThreads = weeks.flatMap(w => w.threads);
  if (allThreads.length === 0) {
    return weeks.map(w => ({
      ...w,
      normalizedViews: 0,
      normalizedEngagement: 0,
    }));
  }

  const threadViewsValues = allThreads.map(t => t.views);
  const threadEngagementValues = allThreads.map(t => t.weightedEngagement);

  const minThreadViews = Math.min(...threadViewsValues);
  const maxThreadViews = Math.max(...threadViewsValues);
  const threadViewsRange = maxThreadViews - minThreadViews || 1;

  const minThreadEngagement = Math.min(...threadEngagementValues);
  const maxThreadEngagement = Math.max(...threadEngagementValues);
  const threadEngagementRange = maxThreadEngagement - minThreadEngagement || 1;

  // 정규화 적용
  return weeks.map(week => {
    // 개별 스레드 정규화
    const normalizedThreads = week.threads.map(thread => ({
      ...thread,
      normalizedViews: (thread.views - minThreadViews) / threadViewsRange,
      normalizedEngagement: (thread.weightedEngagement - minThreadEngagement) / threadEngagementRange,
    }));

    if (week.threadCount === 0) {
      return {
        ...week,
        normalizedViews: 0,
        normalizedEngagement: 0,
        threads: normalizedThreads,
      };
    }

    return {
      ...week,
      normalizedViews: (week.totalViews - minWeekViews) / weekViewsRange,
      normalizedEngagement: (week.weightedEngagement - minWeekEngagement) / weekEngagementRange,
      threads: normalizedThreads,
    };
  });
};

/**
 * 활동 통계 계산
 * @param {Array} weeks - groupThreadsByWeek 결과
 * @returns {Object}
 */
export const calculateActivityStats = (weeks) => {
  const activeWeeks = weeks.filter(w => w.threadCount > 0);
  const totalWeeksActive = activeWeeks.length;

  // 가장 활발했던 주
  let mostActiveWeek = null;
  let maxThreads = 0;
  weeks.forEach(week => {
    if (week.threadCount > maxThreads) {
      maxThreads = week.threadCount;
      mostActiveWeek = week.weekNumber;
    }
  });

  // 최장 연속 활동 주
  let longestStreak = 0;
  let currentStreak = 0;
  weeks.forEach(week => {
    if (week.threadCount > 0) {
      currentStreak++;
      longestStreak = Math.max(longestStreak, currentStreak);
    } else {
      currentStreak = 0;
    }
  });

  // 주당 평균 스레드
  const totalThreads = weeks.reduce((sum, w) => sum + w.threadCount, 0);
  const avgThreadsPerWeek = totalWeeksActive > 0 ? totalThreads / totalWeeksActive : 0;

  return {
    totalWeeksActive,
    mostActiveWeek,
    longestStreak,
    avgThreadsPerWeek: Math.round(avgThreadsPerWeek * 10) / 10,
    totalThreads,
  };
};

/**
 * 월별 주 범위 계산
 * @param {number} year
 * @returns {Array} 12개월 배열, 각 월의 시작/종료 주 번호
 */
export const getMonthWeekRanges = (year) => {
  const ranges = [];

  for (let month = 0; month < 12; month++) {
    // 해당 월의 첫째 날
    const firstDay = new Date(year, month, 1);
    let startWeek = getISOWeekNumber(firstDay);

    // 해당 월의 마지막 날
    const lastDay = new Date(year, month + 1, 0);
    let endWeek = getISOWeekNumber(lastDay);

    // 1월 초: 작년 마지막 주(52/53)에 속할 수 있음 → 1로 보정
    if (month === 0 && startWeek > 50) {
      startWeek = 1;
    }

    // 12월 말: 다음해 1주차에 속할 수 있음 → 52로 보정
    if (month === 11 && endWeek < 5) {
      endWeek = 52;
    }

    ranges.push({
      month,
      startWeek: Math.min(startWeek, 52),
      endWeek: Math.min(endWeek, 52),
    });
  }

  return ranges;
};

/**
 * 전체 활동 요약 계산
 * @param {Array} threads - 전체 스레드 배열
 * @param {number} year
 * @returns {Object|null}
 */
export const computeActivitySummary = (threads, year) => {
  if (!threads || threads.length === 0) return null;

  // 1. 주간 그룹핑 및 집계
  const rawWeeks = groupThreadsByWeek(threads, year);

  // 2. 정규화
  const weeks = normalizeWeeklyData(rawWeeks);

  // 3. 통계 계산
  const stats = calculateActivityStats(weeks);

  // 4. 월별 주 범위
  const monthRanges = getMonthWeekRanges(year);

  // 5. 스케일링 정보
  const activeWeeks = weeks.filter(w => w.threadCount > 0);
  const viewsValues = activeWeeks.map(w => w.totalViews);
  const engagementValues = activeWeeks.map(w => w.weightedEngagement);

  return {
    year,
    weeks,
    stats,
    monthRanges,
    scaling: {
      minViews: viewsValues.length > 0 ? Math.min(...viewsValues) : 0,
      maxViews: viewsValues.length > 0 ? Math.max(...viewsValues) : 0,
      minEngagement: engagementValues.length > 0 ? Math.min(...engagementValues) : 0,
      maxEngagement: engagementValues.length > 0 ? Math.max(...engagementValues) : 0,
    },
  };
};
